import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { setupBearerAuthInterceptor, setupApiKeyAuthInterceptor, setupErrorInterceptor, type TokenRefreshConfig } from './interceptors';
import type { ErrorHandlerOptions } from './errorHandler';

/**
 * Configuration options for creating an API client
 */
export interface ApiClientConfig {
  baseURL: string;
  authType: 'bearer' | 'api-key' | 'none';
  tokenKey?: string; // localStorage key for token (bearer auth)
  apiKey?: string; // API key value (api-key auth)
  onUnauthorized?: () => void;
  errorHandlerOptions?: ErrorHandlerOptions;
  /** Verilirse 401'lerde önce refresh-token ile yenileme denenir (bearer auth) */
  refresh?: Omit<TokenRefreshConfig, 'tokenKey'>;
}

/**
 * Creates a configured Axios instance with appropriate interceptors
 */
export function createApiClient(config: ApiClientConfig): AxiosInstance {
  const axiosConfig: AxiosRequestConfig = {
    baseURL: config.baseURL,
    timeout: 200_000, // 200s — must exceed max server-side query timeout (300s clamp covers payload)
    headers: {
      'Content-Type': 'application/json',
    },
  };

  const client = axios.create(axiosConfig);

  // Setup auth interceptor based on type
  switch (config.authType) {
    case 'bearer':
      setupBearerAuthInterceptor(client, config.tokenKey || 'token');
      break;
    case 'api-key':
      if (config.apiKey) {
        setupApiKeyAuthInterceptor(client, config.apiKey);
      }
      break;
    case 'none':
      // No auth interceptor
      break;
  }

  // Setup error interceptor (+ optional refresh-token flow for bearer auth)
  setupErrorInterceptor(client, {
    onUnauthorized: config.onUnauthorized,
    refresh:
      config.authType === 'bearer' && config.refresh
        ? { ...config.refresh, tokenKey: config.tokenKey || 'token' }
        : undefined,
    ...config.errorHandlerOptions,
  });

  return client;
}

/**
 * Creates an API client with Bearer token authentication
 * Suitable for AdminPanel and CustomerPanel.Besyildiz
 */
export function createBearerAuthClient(
  baseURL: string,
  tokenKey: string = 'token',
  onUnauthorized?: () => void,
  refresh?: Omit<TokenRefreshConfig, 'tokenKey'>
): AxiosInstance {
  return createApiClient({
    baseURL,
    authType: 'bearer',
    tokenKey,
    onUnauthorized,
    refresh,
  });
}

/**
 * Creates an API client with API Key authentication
 * Suitable for CustomerPanel.Template
 */
export function createApiKeyClient(
  baseURL: string,
  apiKey: string
): AxiosInstance {
  return createApiClient({
    baseURL,
    authType: 'api-key',
    apiKey,
  });
}
